/**************************************************************************************************************************************************
*   MODULE NAME     :  SearchAbstractModal
*   AUTHOR          :  GLSD DEV Team
*   DATE            :  18-02-2021
*   DESCRIPTION     :  Modal component to select abstract from CreateNewAbstract
*   Copyright 2021 Eli Lilly and Company
*   All rights reserved
*
/**************************************************************************************************************************************************
*   *** CHANGE HISTORY ***
/**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG1689267
*   DESCRIPTION: <GLSD>: New System Development
/**************************************************************************************************************************************************/

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Button, Modal, Popover, OverlayTrigger} from 'react-bootstrap';
import { bindActionCreators } from "redux";
import { FaSpinner } from "react-icons/fa";
import swal from "sweetalert";
import * as MessagePool from '../../common/MessagesPool';
import {
  loadAllOrignalAbstractData
} from "../../../actions/abstractActions";
import DataTable from '../../common/dataTable/DataTable';

class SearchAbstractModal extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      originalAbstractId: '',
      originalAbstractTitle: '',
      abstractsDataList: [],
      isLoading: false,
      paginationData: {
        searchText: undefined,
        startFrom: 0,
        perPageLimit: 25,
        sortField: "title",
        sortOrder: "desc",
        count: 0,
        page: 1,
        searchCount: 0,
      }
    }
  }

  setIsLoading = (isLoading) => {
    this.setState({isLoading})
  } 

  loadData = ({
    searchText,
    sortField,
    sortOrder,
    filters,
    page,
    sizePerPage,
  }) => {
    const startFrom = (page - 1) * sizePerPage;
    const paginationData = {
      searchText,
      startFrom,
      perPageLimit: sizePerPage,
      sortField,
      sortOrder,
      page,
    };
    this.setState({ paginationData },this.loadAbstracts);
  };
  openAbstractDetailWindow = (abstractId) => {
    window.open(`/abstractDetail/${abstractId}`, "", `width=${window.innerWidth},height=${window.innerHeight}`);
  }

  handleOnSelect = (row, isSelect) => {
    this.setState({
      originalAbstractId: row.abstractId,
      originalAbstractTitle: row.title
    })
  }

  loadAbstracts = () => {
    this.setIsLoading(true);
    let {abstractId} = this.props;
    let {paginationData} = this.state;
    let payload = { originalAbstractIsNull: true, abstractId, paginationData }
    this.props.loadAllOrignalAbstractData(payload).then(res => {
      if (res && res.data && res.data.result) {
        let data = res.data.result;
        const { count, searchCount} = data;
        this.setState({
          paginationData: {...paginationData, count, searchCount},
          abstractsDataList: data.list
        })
        this.setIsLoading(false);
      } else {
        this.setIsLoading(false);
        swal('', MessagePool.SERVER_ERROR, 'error');
      }
    }).catch(e => {
      this.setIsLoading(false);
      swal('', MessagePool.SERVER_ERROR, 'error');
    })
  }
  
  
  render() {
    const {
      showSearchAbstractModal,
      onHide,
      onOk
    } = this.props

    const {
      abstractsDataList,
      paginationData: { page, count, searchCount },
      originalAbstractId,
      originalAbstractTitle,
      isLoading
    } = this.state;
    
    
    const selectRow = {
      mode: 'radio',
      selectionHeaderRenderer: () => 'X',
      onSelect: this.handleOnSelect,
      hideSelectAll: true
    };
    // Appends ellipsis (...) on text based on charLimit
    const ellipsisFormatter = (content, charLimit) => {
      if (content.length > charLimit) {
        return <span>{content.substr(0, charLimit)}...</span>
      } else {
        return content;
      }
    }
    const abstractDataColumns = [
      {
        dataField: "abstractId",
        hidden: true,
        sort: true,
      },
      {
        dataField: "updatedAt",
        hidden: true,
        sort: true,
      },
      {
        dataField: 'title',
        text: 'Title',
        sort: true,
        align: 'left',
        classes: 'custom-link',
        formatter: (cellContent, row) => {
          const _popOver = (
            <OverlayTrigger
              placement="right"
              popperConfig={{
                modifiers: {
                  preventOverflow: {
                    enabled: false,
                  },
                },
              }}
              overlay={
                <Popover style={{ maxWidth: "50vw" }} id="popover-contained">
                  <Popover.Content>{row.title}</Popover.Content>
                </Popover>
              }
            >
              <span
                className="fake-link"
                onClick={() => this.openAbstractDetailWindow(row.abstractId)}
              >
                {row.title&& row.title.length <= 70
                  ? row.title
                  : row.title && row.title.substr(0, 70) + "..."}
              </span>
            </OverlayTrigger>
          );
          return row && row.title ? _popOver : "";
        },
        attrs: (cell, row) => {
          return { title: row && row.title ? row.title : '' }
        }
      },
      {
        dataField: 'authors',
        text: 'Author(s)',
        sort: true,
        formatter: (cellContent, row) => {
          const _popOver = (
            <OverlayTrigger
              placement="right"
              popperConfig={{
                modifiers: {
                  preventOverflow: {
                    enabled: false,
                  },
                },
              }}
              overlay={
                <Popover style={{ maxWidth: "40vw" }} id="popover-contained">
                  <Popover.Content>{row.authors}</Popover.Content>
                </Popover>
              }
            >
              <div>
              {row.authors&& row.authors.length <= 70
                  ? row.authors
                  : row.authors && row.authors.substr(0, 70) + "..."}
              </div>
            </OverlayTrigger>
          );
          return row && row.authors ? _popOver : "";
        },
        attrs: (cell, row) => {
          return { title: row && row.authors ? row.authors : '' }
        }
      },
      {
        dataField: 'source',
        text: 'Source',
        sort: true,
        formatter: (cellContent, row) => {
          const _popOver = (
            <OverlayTrigger
              placement="right"
              popperConfig={{
                modifiers: {
                  preventOverflow: {
                    enabled: false,
                  },
                },
              }}
              overlay={
                <Popover style={{ maxWidth: "40vw" }} id="popover-contained">
                  <Popover.Content>{row.source}</Popover.Content>
                </Popover>
              }
            >
              <div>
              {row.source && row.source.length <= 70
                  ? row.source
                  : row.source && row.source.substr(0, 70) + "..."}
              </div>
            </OverlayTrigger>
          );
          return row && row.source ? _popOver : "";
        },
        attrs: (cell, row) => {
          return { title: row && row.source ? row.source : '' }
        }
      },
      {
        dataField: 'institution',
        text: 'Institution',
        sort: true,
        formatter: (cellContent, row) => {
          const _popOver = (
            <OverlayTrigger
              placement="right"
              popperConfig={{
                modifiers: {
                  preventOverflow: {
                    enabled: false,
                  },
                },
              }}
              overlay={
                <Popover style={{ maxWidth: "40vw" }} id="popover-contained">
                  <Popover.Content>{row.institution}</Popover.Content>
                </Popover>
              }
            >
              <div>
              {row.institution && row.institution.length <= 70
                  ? row.institution
                  : row.institution && row.institution.substr(0, 70) + "..."}
              </div>
            </OverlayTrigger>
          );
          return row && row.institution ? _popOver : "";
        },
        attrs: (cell, row) => {
          return { title: row && row.institution ? row.institution : '' }
        }
      },
    ];
  
    return (
      <div>
        <Modal size='lg' className="modal-1225" show={showSearchAbstractModal} onHide={() => onHide()} centered backdrop="static" autoFocus >
          <Modal.Header closeButton style={{ backgroundColor: '#00415f' }} >
            <Modal.Title style={{ color: '#fff' }}>Search Original Abstracts</Modal.Title>
          </Modal.Header>
          <Modal.Body >
            <div className="leftAbstractList" style={{ marginTop: '30px' }}>
              <DataTable
                keyField="abstractId"
                defaultSortField="abstractId"
                data={abstractsDataList}
                columns={abstractDataColumns}
                csvName={false}
                selectRow={selectRow}
                loadData={this.loadData}
                totalSize={count}
                searchCount={searchCount}
                page={page}
                remote
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="primary" onClick={() => onOk({ originalAbstractId, originalAbstractTitle })}>OK</Button>
            <Button type="button" variant="primary" onClick={() => onHide()} >Cancel</Button>
          </Modal.Footer>
        </Modal>
        {isLoading && (
          <div className="loader">
            <FaSpinner />
          </div>
        )}
      </div>
    );
  }

}


const mapStateToProps = state => {
  return {
  }
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      loadAllOrignalAbstractData
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchAbstractModal);